export const RENDER_MODES = Object.freeze(['realistic', 'tactical', 'structure', 'gray']);

export const RENDER_QUALITY_PROFILES = Object.freeze({
  performance: Object.freeze({
    pixelRatio: 1, ssao: false, fxaa: true, kernelRadius: 4, exposure: 1.06, sharpness: 0.1,
  }),
  balanced: Object.freeze({
    pixelRatio: 1.5, ssao: true, fxaa: true, kernelRadius: 7, exposure: 1.12, sharpness: 0.34,
  }),
  quality: Object.freeze({
    pixelRatio: 2.25, ssao: true, fxaa: false, kernelRadius: 9, exposure: 1.16, sharpness: 0.48,
  }),
});

export function resolveRenderQuality(qualityId = 'balanced') {
  return RENDER_QUALITY_PROFILES[qualityId] || RENDER_QUALITY_PROFILES.balanced;
}

export function shouldShowMapEdges(styleId = 'realistic', qualityId = 'balanced') {
  if (qualityId === 'performance' || styleId === 'realistic') return false;
  return styleId === 'tactical' || styleId === 'structure' || styleId === 'gray';
}

const SURFACE_APPEARANCES = Object.freeze([
  Object.freeze({
    family: 'hidden', tokens: ['audioblocker'], visible: false,
    tint: 0x000000, tintStrength: 0, roughness: 1, metalness: 0,
    detailScale: 1, detailStrength: 0, mapAffinity: 0,
  }),
  Object.freeze({
    family: 'metal',
    tokens: ['metal', 'brass', 'canister', 'computer', 'chainlink'],
    visible: true, tint: 0x708995, tintStrength: 0.62,
    roughness: 0.48, metalness: 0.28, detailScale: 0.76,
    detailStrength: 0.72, mapAffinity: 0.04,
  }),
  Object.freeze({
    family: 'wood', tokens: ['wood', 'cardboard', 'paper'],
    visible: true, tint: 0x8b5c36, tintStrength: 0.54,
    roughness: 0.8, metalness: 0, detailScale: 1.18,
    detailStrength: 1.12, mapAffinity: 0.08,
  }),
  Object.freeze({
    family: 'stone',
    tokens: ['rock', 'boulder', 'concrete', 'brick', 'plaster', 'sheetrock', 'clay', 'porcelain', 'pottery'],
    visible: true, tint: 0x8a8174, tintStrength: 0.2,
    roughness: 0.9, metalness: 0, detailScale: 1.34,
    detailStrength: 1.18, mapAffinity: 0.56,
  }),
  Object.freeze({
    family: 'ground', tokens: ['sand', 'dirt', 'carpet', 'tile'],
    visible: true, tint: 0xb59a6b, tintStrength: 0.24,
    roughness: 0.94, metalness: 0, detailScale: 1.52,
    detailStrength: 0.82, mapAffinity: 1,
  }),
  Object.freeze({
    family: 'soft', tokens: ['rubber', 'plastic', 'cloth', 'upholstery'],
    visible: true, tint: 0x536169, tintStrength: 0.46,
    roughness: 0.86, metalness: 0, detailScale: 0.7,
    detailStrength: 0.5, mapAffinity: 0.03,
  }),
]);

const DEFAULT_SURFACE_APPEARANCE = Object.freeze({
  family: 'default', tokens: [], visible: true,
  tint: 0xffffff, tintStrength: 0,
  roughness: null, metalness: null, detailScale: 1,
  detailStrength: 1, mapAffinity: 0.62,
});

export function resolveSurfaceAppearance(surfaceName = 'default') {
  const normalized = String(surfaceName).trim().toLowerCase();
  return SURFACE_APPEARANCES.find((appearance) => (
    appearance.tokens.some((token) => normalized.includes(token))
  )) || DEFAULT_SURFACE_APPEARANCE;
}

const MAP_PALETTES = Object.freeze({
  de_dust2: {
    groundLow: 0x9b7a52, groundHigh: 0xd8ba83,
    wallLow: 0x75563b, wallHigh: 0xb58a5b,
    roofLow: 0x57473b, roofHigh: 0x8a6b50,
    road: 0x746c60, accent: 0x4f8895,
    edge: 0xe2c692, background: 0x11171a,
    hemisphereSky: 0xb9dcf0, hemisphereGround: 0x382c22,
    sunColor: 0xffe1a8, fillColor: 0x78aabd,
  },
  de_mirage: {
    groundLow: 0x9c7d50, groundHigh: 0xd7bd82,
    wallLow: 0x714a31, wallHigh: 0xb67d4f,
    roofLow: 0x653d31, roofHigh: 0x9d5b45,
    road: 0x786f63, accent: 0x49a0ae,
    edge: 0xe1c687, background: 0x101619,
    hemisphereSky: 0xb4d8ea, hemisphereGround: 0x34291f,
    sunColor: 0xffdda5, fillColor: 0x6ca5b6,
  },
  de_inferno: {
    groundLow: 0x756b5e, groundHigh: 0xafa18c,
    wallLow: 0x76503b, wallHigh: 0xbc8060,
    roofLow: 0x562b2b, roofHigh: 0xa7483d,
    road: 0x686862, accent: 0x4b8b68,
    edge: 0xd8b68e, background: 0x141617,
    hemisphereSky: 0xc1d5dc, hemisphereGround: 0x302621,
    sunColor: 0xffc98f, fillColor: 0x6a9f88,
  },
  de_anubis: {
    groundLow: 0x9a7042, groundHigh: 0xd5ad6c,
    wallLow: 0x6f4c2e, wallHigh: 0xb68049,
    roofLow: 0x4e4033, roofHigh: 0x7f674b,
    road: 0x716352, accent: 0x2d8e9f,
    edge: 0xe6c27e, background: 0x10191b,
    hemisphereSky: 0xb4ddea, hemisphereGround: 0x38271b,
    sunColor: 0xffd992, fillColor: 0x49a6b4,
  },
  de_ancient: {
    groundLow: 0x4d5740, groundHigh: 0x84906a,
    wallLow: 0x42483c, wallHigh: 0x777766,
    roofLow: 0x3b4037, roofHigh: 0x626858,
    road: 0x5e5b4e, accent: 0x699451,
    edge: 0xabb68d, background: 0x0f1512,
    hemisphereSky: 0xb5cfca, hemisphereGround: 0x20291e,
    sunColor: 0xd8ddb1, fillColor: 0x6e9e83,
  },
  de_nuke: {
    groundLow: 0x58636b, groundHigh: 0x9ca8ad,
    wallLow: 0x3d4b55, wallHigh: 0x77909e,
    roofLow: 0x39434a, roofHigh: 0x687781,
    road: 0x4f5a61, accent: 0x3aa7c4,
    edge: 0xc7dce3, background: 0x0e151a,
    hemisphereSky: 0xc4e2ee, hemisphereGround: 0x222a2e,
    sunColor: 0xf3fbff, fillColor: 0x69b7d0,
  },
  de_vertigo: {
    groundLow: 0x5f656a, groundHigh: 0x9ea5aa,
    wallLow: 0x454d54, wallHigh: 0x7b858c,
    roofLow: 0x3d454c, roofHigh: 0x68737b,
    road: 0x555d63, accent: 0xd0a832,
    edge: 0xcad6da, background: 0x101519,
    hemisphereSky: 0xc1d8e2, hemisphereGround: 0x252b2f,
    sunColor: 0xe8eef0, fillColor: 0xe0b84c,
  },
});

const MODE_DEFAULTS = Object.freeze({
  realistic: {
    buyzoneColor: 0x35e08d,
    colorStrength: 1.0, detailStrength: 0.56, contourStrength: 0.055,
    wallStrength: 0.035, semanticStrength: 0.88,
    roughness: 0.82, metalness: 0.015, envMapIntensity: 0.34,
    outlineOpacity: 0, fogDensity: 0.0024, mapTextureBlend: 0.48,
    ambientIntensity: 0.28, hemisphereIntensity: 1.24,
    sunIntensity: 2.35, fillIntensity: 0.48, exposure: 1.16,
  },
  tactical: {
    buyzoneColor: 0x48e0ff,
    groundLow: 0x213746, groundHigh: 0x6c9aae,
    wallLow: 0x10202d, wallHigh: 0x31596b,
    roofLow: 0x1b3342, roofHigh: 0x487286,
    road: 0x314b59, accent: 0x45d5de,
    edge: 0x62d9e8, background: 0x08121b,
    hemisphereSky: 0x9bdcff, hemisphereGround: 0x121a24,
    sunColor: 0xe7f7ff, fillColor: 0x5bb8ff,
    colorStrength: 0.98, detailStrength: 0.16, contourStrength: 0.11,
    wallStrength: 0.075, semanticStrength: 0.52,
    roughness: 0.76, metalness: 0.03, envMapIntensity: 0.42,
    outlineOpacity: 0.09, fogDensity: 0.0032, mapTextureBlend: 0.3,
    ambientIntensity: 0.32, hemisphereIntensity: 1.18,
    sunIntensity: 2.15, fillIntensity: 0.55, exposure: 1.12,
  },
  structure: {
    buyzoneColor: 0xffb44a,
    groundLow: 0x27394a, groundHigh: 0xa8c9d6,
    wallLow: 0x101923, wallHigh: 0x526f7d,
    roofLow: 0x344858, roofHigh: 0x7893a0,
    road: 0x384c58, accent: 0xffb44a,
    edge: 0xa3f5ff, background: 0x060d14,
    hemisphereSky: 0xb8e7f3, hemisphereGround: 0x101923,
    sunColor: 0xf2fbff, fillColor: 0x63c4e4,
    colorStrength: 1.0, detailStrength: 0.08, contourStrength: 0.2,
    wallStrength: 0.16, semanticStrength: 0.68,
    roughness: 0.72, metalness: 0.02, envMapIntensity: 0.4,
    outlineOpacity: 0.24, fogDensity: 0.002, mapTextureBlend: 0.58,
    ambientIntensity: 0.3, hemisphereIntensity: 1.22,
    sunIntensity: 2.25, fillIntensity: 0.58, exposure: 1.14,
  },
  gray: {
    buyzoneColor: 0x8fa4ad,
    groundLow: 0x4a535c, groundHigh: 0x929ca4,
    wallLow: 0x252c32, wallHigh: 0x606b73,
    roofLow: 0x343c43, roofHigh: 0x717b82,
    road: 0x4f585f, accent: 0xaab6bd,
    edge: 0xb8c5cf, background: 0x0a1017,
    hemisphereSky: 0xc7d2d8, hemisphereGround: 0x171d22,
    sunColor: 0xf0f2f3, fillColor: 0x8797a1,
    colorStrength: 0.92, detailStrength: 0.05, contourStrength: 0.05,
    wallStrength: 0.045, semanticStrength: 0.2,
    roughness: 0.94, metalness: 0.0, envMapIntensity: 0.26,
    outlineOpacity: 0.04, fogDensity: 0.0028, mapTextureBlend: 0.12,
    ambientIntensity: 0.36, hemisphereIntensity: 1.05,
    sunIntensity: 1.85, fillIntensity: 0.35, exposure: 1.08,
  },
});

export function getMapPalette(mapId) {
  return MAP_PALETTES[mapId] || MAP_PALETTES.de_mirage;
}

export function resolveRenderProfile(mapId, mode = 'realistic') {
  const normalizedMode = RENDER_MODES.includes(mode) ? mode : 'realistic';
  const modeDefaults = MODE_DEFAULTS[normalizedMode];
  if (normalizedMode !== 'realistic') return { ...modeDefaults, mode: normalizedMode, mapId };
  return {
    ...getMapPalette(mapId),
    ...modeDefaults,
    mode: normalizedMode,
    mapId,
  };
}

export function validateRenderProfiles() {
  const required = [
    'groundLow', 'groundHigh', 'wallLow', 'wallHigh', 'roofLow', 'roofHigh',
    'road', 'accent', 'edge', 'background', 'hemisphereSky', 'hemisphereGround',
    'sunColor', 'fillColor', 'buyzoneColor', 'detailStrength', 'semanticStrength', 'mapTextureBlend',
  ];
  return Object.keys(MAP_PALETTES).every((mapId) => RENDER_MODES.every((mode) => {
    const profile = resolveRenderProfile(mapId, mode);
    return required.every((key) => profile[key] !== undefined && profile[key] !== null);
  }));
}

export const SUPPORTED_MAP_PALETTES = Object.freeze(Object.keys(MAP_PALETTES));
